import { useNavigate } from 'react-router-dom';
import {
  Zap, Download, Code, Database,
  CheckCircle, ArrowRight
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } =
    useAuth0();

  // ── Auth-gated navigation ─────────────────────
  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/generator');
    } else {
      loginWithRedirect({
        appState: { returnTo: '/generator' }
      });
    }
  };

  const handlePricing = (plan: string) => {
    if (plan === 'Contact Sales') {
      navigate('/contact');
      return;
    }
    if (isAuthenticated) {
      navigate('/generator');
    } else {
      loginWithRedirect({
        appState: { returnTo: '/generator' }
      });
    }
  };

    const handlePricingClick = () => {
    navigate('/');
    // Wait for page to load then scroll ✅
    setTimeout(() => {
      document.getElementById('pricing')
        ?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-950">

      {/* ── Hero ── */}
      <section className="relative px-4 pt-24
                          pb-20 text-center
                          overflow-hidden">

        {/* Background glow */}
        <div className="absolute inset-0 flex
                        items-center justify-center
                        pointer-events-none">
          <div className="w-96 h-96 bg-blue-500
                          opacity-5 rounded-full
                          blur-3xl"/>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center
                        gap-2 bg-blue-500/10
                        border border-blue-500/20
                        text-blue-400 text-xs
                        font-mono px-4 py-2
                        rounded-full mb-8">
          <span className="w-2 h-2 bg-green-400
                           rounded-full animate-pulse"/>
          v1.0 — Now Available
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl
                       font-bold mb-6 leading-tight
                       tracking-tight">
          <span className="text-blue-200">
            Schema to
          </span>
          <br />
          <span className="text-blue-400">
            Production API
          </span>
          <br />
          <span className="text-blue-300">
            in 30 Seconds
          </span>
        </h1>

        {/* Subtext */}
        <p className="text-gray-400 text-xl
                      max-w-2xl mx-auto mb-10
                      leading-relaxed">
          Paste your OpenAPI spec or SQL schema.
          Get a complete, downloadable{' '}
          <span className="text-blue-300 font-semibold">
            .NET 8
          </span>{' '}
          or{' '}
          <span className="text-blue-300 font-semibold">
            Java Spring Boot (Maven)
          </span>{' '}
          project instantly.
        </p>

        {/* CTA Buttons ✅ */}
        <div className="flex items-center
                        justify-center
                        gap-4 flex-wrap">
          <button
            onClick={handleCTA}
            className="flex items-center gap-2
                       bg-blue-600 hover:bg-blue-500
                       text-white font-semibold
                       px-8 py-4 rounded-xl
                       transition-all duration-200
                       hover:shadow-lg
                       hover:shadow-blue-500/25
                       hover:-translate-y-0.5">
            <Zap className="w-5 h-5" />
            Generate Now — Free
          </button>

          <a
            href="#how-it-works"
            className="flex items-center gap-2
                       bg-gray-800 hover:bg-gray-700
                       text-gray-300 font-semibold
                       px-8 py-4 rounded-xl
                       border border-gray-700
                       transition-all duration-200">
            See How It Works
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Terminal Demo */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="bg-gray-900 border
                          border-gray-800 rounded-2xl
                          overflow-hidden shadow-2xl
                          text-left">

            {/* Terminal header */}
            <div className="flex items-center gap-2
                            px-4 py-3 bg-gray-800
                            border-b border-gray-700">
              <div className="w-3 h-3 rounded-full bg-red-500"/>
              <div className="w-3 h-3 rounded-full bg-yellow-500"/>
              <div className="w-3 h-3 rounded-full bg-green-500"/>
              <span className="font-mono text-xs
                               text-gray-500 ml-2">
                scaffoldbridge — generate
              </span>
            </div>

            {/* Terminal body */}
            <div className="p-6 font-mono text-sm
                            leading-relaxed">
              <div className="text-gray-600">
                // POST /api/generate
              </div>
              <div className="text-gray-400 mt-2">{'{'}</div>
              <div className="ml-4">
                <span className="text-blue-400">"inputSql"</span>
                <span className="text-gray-400">: "</span>
                <span className="text-green-400">CREATE TABLE Products (...)</span>
                <span className="text-gray-400">",</span>
              </div>
              <div className="ml-4">
                <span className="text-blue-400">"target"</span>
                <span className="text-gray-400">: </span>
                <span className="text-yellow-400">0</span>
                <span className="text-gray-400">,</span>
              </div>
              <div className="ml-4">
                <span className="text-blue-400">"rootNamespace"</span>
                <span className="text-gray-400">: </span>
                <span className="text-green-400">"ECommerce.API"</span>
              </div>
              <div className="text-gray-400">{'}'}</div>

              <div className="mt-4 text-yellow-400">
                ▶ Parsing SQL schema...
              </div>
              <div className="text-yellow-400">
                ▶ Detected 2 entities: Product, Category
              </div>
              <div className="text-yellow-400">
                ▶ Generating .NET 8 Web API...
              </div>
              <div className="text-yellow-400">
                ▶ Creating ZIP (14 files)...
              </div>
              <div className="mt-2 text-green-400 font-semibold">
                ✓ ECommerce.API_scaffoldbridge.zip — Ready! (48kb)
              </div>
              <div className="text-gray-600">
                Generated in 0.34s
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="border-y border-gray-800
                          bg-gray-900/50 py-10">
        <div className="max-w-4xl mx-auto px-4
                        grid grid-cols-2
                        md:grid-cols-4 gap-8
                        text-center">
          {[
            { number: '30s',  label: 'Generation time' },
            { number: '2',    label: 'Frameworks' },
            { number: '14+',  label: 'Files generated' },
            { number: '0',    label: 'Setup required' },
          ].map(stat => (
            <div key={stat.label}>
              <div className="text-3xl font-bold
                              text-blue-400 mb-1">
                {stat.number}
              </div>
              <div className="text-gray-500 text-sm">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-400 font-mono
                            text-sm mb-3">
              // How it works
            </div>
            <h2 className="text-4xl font-bold
                           text-blue-200">
              Three steps to a production-ready project
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Paste Your Schema',
                desc: 'Drop in your OpenAPI JSON or SQL CREATE TABLE statements. ScaffoldBridge auto-detects the format automatically.',
                icon: <Database className="w-6 h-6" />
              },
              {
                step: '02',
                title: 'Choose Your Stack',
                desc: 'Select .NET 8 or Java Spring Boot (Maven). Set your namespace, database provider, and optional features.',
                icon: <Code className="w-6 h-6" />
              },
              {
                step: '03',
                title: 'Download & Run',
                desc: 'Click Generate. Download your ZIP. Extract, open in Visual Studio or IntelliJ, and run immediately.',
                icon: <Download className="w-6 h-6" />
              },
            ].map(item => (
              <div key={item.step}
                   className="bg-gray-900 border
                              border-gray-800 rounded-2xl
                              p-8 hover:border-blue-800
                              transition-colors">
                <div className="flex items-center
                                justify-center w-10 h-10
                                bg-blue-500/10
                                border border-blue-500/20
                                rounded-xl text-blue-400
                                font-mono text-sm
                                font-bold mb-6">
                  {item.step}
                </div>
                <div className="text-blue-400 mb-3">
                  {item.icon}
                </div>
                <h3 className="text-blue-200 font-bold
                               text-lg mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm
                              leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-24 px-4 bg-gray-900/50
                          border-y border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-400 font-mono
                            text-sm mb-3">
              // Features
            </div>
            <h2 className="text-4xl font-bold
                           text-blue-200">
              Everything included. Nothing to configure.
            </h2>
          </div>

          <div className="grid md:grid-cols-2
                          lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Dual Framework',
                desc: 'Generate .NET 8 AND Java Spring Boot (Maven) from the same schema. No other tool does this.',
                icon: '⚡'
              },
              {
                title: 'Auto Detection',
                desc: 'Paste OpenAPI JSON or SQL DDL — ScaffoldBridge detects the format automatically every time.',
                icon: '🔍'
              },
              {
                title: 'SQL Scripts',
                desc: 'Every ZIP includes create_tables.sql, drop_tables.sql and seed_data.sql ready to run.',
                icon: '🗄️'
              },
              {
                title: 'GitHub Push + Docker',
                desc: 'Push directly to GitHub with one click. CI/CD pipeline runs automatically. Docker image built to ghcr.io.',
                icon: '🐳'
              },
              {
                title: 'Production Patterns',
                desc: 'SOLID principles, service layers, interfaces, DTOs, error handling and Swagger included.',
                icon: '✅'
              },
              {
                title: 'Zero Setup',
                desc: 'No CLI to install. No config files. Works in any browser. Runs on the first try.',
                icon: '🚀'
              },
            ].map(feature => (
              <div key={feature.title}
                   className="bg-gray-900 border
                              border-gray-800 rounded-2xl
                              p-6 hover:border-blue-800
                              transition-colors">
                <div className="text-3xl mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-blue-200
                               font-bold mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm
                              leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24 px-4 scroll-mt-20" >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-400 font-mono
                            text-sm mb-3">
              // Pricing
            </div>
            <h2 className="text-4xl font-bold
                           text-blue-200">
              Simple, transparent pricing
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Starter',
                price: '$0',
                desc: 'Try ScaffoldBridge free',
                features: [
                  '5 generations/month',
                  '.NET 8 generation',
                  'Java generation',
                  'ZIP download',
                ],
                notIncluded: [
                  'SQL scripts',
                  'API access',
                  'GitHub push',
                ],
                cta: 'Get Started Free',
                featured: false
              },
              {
                name: 'Pro',
                price: '$49',
                desc: 'For developers and consultants',
                features: [
                  'Unlimited generations',
                  '.NET 8 + Java both',
                  'SQL scripts included',
                  'REST API access',
                  'GitHub push delivery',
                  'Priority support',
                ],
                notIncluded: [],
                cta: 'Start Free Trial',
                featured: true
              },
              {
                name: 'Team',
                price: '$149',
                desc: 'For teams and companies',
                features: [
                  'Everything in Pro',
                  'Up to 10 seats',
                  'Team workspace',
                  'Shared templates',
                  'Usage analytics',
                  'Dedicated support',
                ],
                notIncluded: [],
                cta: 'Contact Sales',
                featured: false
              },
            ].map(plan => (
              <div key={plan.name}
                   className={`relative rounded-2xl
                               p-8 border transition-all ${
                plan.featured
                  ? 'bg-gray-900 border-blue-500 shadow-lg shadow-blue-500/10'
                  : 'bg-gray-900 border-gray-800'
              }`}>

                {plan.featured && (
                  <div className="absolute -top-4
                                  left-1/2 -translate-x-1/2
                                  bg-blue-600 text-white
                                  text-xs font-bold
                                  px-4 py-1.5 rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="text-blue-200 font-bold
                                text-xl mb-1">
                  {plan.name}
                </div>
                <div className="text-4xl font-bold
                                text-white mb-1">
                  {plan.price}
                  <span className="text-gray-500 text-base
                                   font-normal">
                    /mo
                  </span>
                </div>
                <div className="text-gray-500 text-sm mb-6">
                  {plan.desc}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map(f => (
                    <li key={f}
                        className="flex items-center
                                   gap-2 text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4
                                              text-green-400
                                              shrink-0" />
                      {f}
                    </li>
                  ))}
                  {plan.notIncluded.map(f => (
                    <li key={f}
                        className="flex items-center
                                   gap-2 text-sm text-gray-600">
                      <span className="w-4 h-4 text-center
                                       shrink-0">✗</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Auth-gated pricing buttons ✅ */}
                <button
                  onClick={() => handlePricing(plan.cta)}
                  className={`w-full text-center py-3
                              rounded-xl font-semibold
                              text-sm transition-colors ${
                    plan.featured
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                  }`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-4 text-center
                          border-t border-gray-800">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold
                         text-blue-200 mb-4">
            Stop writing boilerplate.
          </h2>
          <h2 className="text-4xl font-bold
                         text-blue-400 mb-6">
            Start shipping features.
          </h2>
          <p className="text-gray-500 mb-10">
            Join developers who generate
            production-ready .NET 8 and Java APIs
            in seconds — not days.
          </p>

          {/* Auth-gated final CTA ✅ */}
          <button
            onClick={handleCTA}
            className="inline-flex items-center
                       gap-2 bg-blue-600
                       hover:bg-blue-500
                       text-white font-bold
                       px-10 py-4 rounded-xl
                       transition-all duration-200
                       hover:shadow-lg
                       hover:shadow-blue-500/25
                       hover:-translate-y-0.5">
            <Zap className="w-5 h-5" />
            Generate Your First Project Free
          </button>
        </div>
      </section>

    </div>
  );
};

export default HomePage;